
import { io } from 'socket.io-client';

let socket = null;
const listeners = new Map();


/**
 * Connect to the socket server. If already connected, returns the existing socket.
 * @param {string} url Socket server URL
 * @param {object} options Additional options passed to io()
 * @returns {Socket} socket instance
 */
const connect = (url, token) => {
  if (socket && socket.connected) return socket;

  const user = JSON.parse(localStorage.getItem("user"));

  socket = io(url, {
    auth: {
      token: token,
      user_id: user?.id || user?._id,
      department_unit: user?.departments_units?.[0] || user?.department_unit,
      departments_units: user?.departments_units,
      client_type: 'web',
    },
    autoConnect: true,
    transports: ['polling', 'websocket'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelayMax: 5000
  });

  // Re-attach stored listeners after each successful connection (covers reconnect)
  socket.on('connect', () => {
    listeners.forEach((set, event) => {
      set.forEach(cb => {
        // ensure we don't add duplicate handlers on reconnect
        try { socket.off(event, cb); } catch (e) { /* ignore */ }
        socket.on(event, cb);
      });
    });
    flushQueue();
  });

  socket.on('connect_error', (err) => {
    console.error('[socketService] connect_error', err);
  });

  return socket;
};

const disconnect = () => {
  if (!socket) return;
  try {
    socket.disconnect();
  } catch (e) {
    console.warn('[socketService] disconnect error', e);
  }
  socket = null;
};

/**
 * Subscribe to an event. Returns an unsubscribe function.
 * @param {string} event
 * @param {Function} cb
 * @returns {Function} unsubscribe
 */
const on = (event, cb) => {
  if (!listeners.has(event)) listeners.set(event, new Set());
  listeners.get(event).add(cb);
  if (socket) socket.on(event, cb);
  return () => off(event, cb);
};

/**
 * Unsubscribe a callback for an event. If cb is omitted, removes all callbacks for the event.
 * @param {string} event
 * @param {Function} [cb]
 */
const off = (event, cb) => {
  if (!listeners.has(event)) return;
  if (typeof cb === 'function') {
    listeners.get(event).delete(cb);
    if (socket) socket.off(event, cb);
  } else {
    // remove all callbacks for event
    const set = listeners.get(event);
    if (socket) {
      set.forEach(fn => socket.off(event, fn));
    }
    listeners.delete(event);
  }
};

let emitQueue = [];

const emit = (event, data) => {
  if (!socket || !socket.connected) {
    console.log(`⏳ [socketService] Queuing emit: ${event}`);
    emitQueue.push({ event, data });
    return;
  }
  socket.emit(event, data);
};

// Update the connect function logic (internal) to flush queue
const flushQueue = () => {
  if (socket && socket.connected && emitQueue.length > 0) {
    console.log(`🚀 [socketService] Flushing ${emitQueue.length} queued emits`);
    emitQueue.forEach(({ event, data }) => socket.emit(event, data));
    emitQueue = [];
  }
};

const isConnected = () => !!(socket && socket.connected);

export default {
  connect,
  disconnect,
  on,
  off,
  emit,
  isConnected,
};
